<script lang="ts">
	import { override } from '$src/lib/utils/classnames'
	import { onDestroy, onMount } from 'svelte'
	import DOMPurify from 'dompurify'
	import { twMerge } from 'tailwind-merge'
	import { fixTitleCharacters } from '$lib/utils/title-case'
	import debounce from 'lodash-es/debounce'

	interface Props {
		title: string
		tripTitle?: string | null
		class?: string
		titleClassNames?: string
		itineraryId?: string | null
		errorOnNull?: boolean
		onupdateTitle?: (title: string, error: boolean) => void
	}

	let {
		title = 'Add a Name',
		tripTitle = null,
		class: className,
		titleClassNames = '',
		itineraryId = null,
		errorOnNull = true,
		onupdateTitle,
	}: Props = $props()

	let inputElement: HTMLInputElement = $state(null)
	let error: string = $state('')

	const saveTitle = () => {
		let newTitle = DOMPurify.sanitize(inputElement?.value?.trim())
		// console.log('saveTitle', newTitle, errorOnNull)

		if (newTitle?.length < 3) {
			error = 'Must be at least 3 characters'
			// return // Do not save the title if validation fails
		} else if (newTitle.length > 50) {
			error = 'Must be less than 50 characters'
			// return // Do not save the title if validation fails
		} else if (
			errorOnNull &&
			(newTitle === 'New Trip' ||
				newTitle === 'Add a Name' ||
				newTitle === 'not set' ||
				newTitle === null ||
				newTitle === '')
		) {
			error = 'This field is required'
		} else {
			error = ''
		}

		title = newTitle

		onupdateTitle?.(newTitle, error !== '')
	}

	const handleKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter') {
			saveTitle()
		}
	}

	let containerClasses = $derived(
		twMerge(
			override(
				'flex flex-col items-start mt-4 -mb-1 lg:mt-7 space-y-0 lg:space-y-1 w-full',
				className,
			),
		),
	)

	let condensedClasses = $derived(
		twMerge(
			override(
				override(
					'transition-all text-sm lg:text-xl tracking-tighter truncate cursor-pointer p-0 bg-transparent w-full border-none active:ring-transparent active:border-none outline-none ring-0 focus:ring-transparent focus:border-none focus:outline-none',
					title === 'New Trip' ||
						title === 'Add a Name' ||
						title === 'not set' ||
						title === null ||
						title === ''
						? 'text-brand-gray-4 dark:text-brand-gray-3 hover:text-black dark:hover:text-white'
						: 'text-brand-gray-5 dark:text-brand-gray-2 hover:text-black dark:hover:text-white',
				),
				titleClassNames,
			),
		),
	)

	let previousItineraryId: string | null = $state(itineraryId)
	let previousTitle: string = $state(title)

	const debouncedSave = debounce(() => {
		if (
			fixTitleCharacters(inputElement?.value)?.trim() !==
				fixTitleCharacters(previousTitle).trim() &&
			itineraryId === previousItineraryId
		) {
			saveTitle()
			previousTitle = inputElement?.value?.trim()
			previousItineraryId = itineraryId
		}
	}, 2000) // 2 seconds delay

	// Create the debounced function when component mounts
	$effect(() => {
		if (inputElement) {
			debouncedSave()
		}
	})

	$effect(() => {
		previousTitle = title
		previousItineraryId = itineraryId
	})

	onMount(() => {
		previousTitle = title
		previousItineraryId = itineraryId
	})

	onDestroy(() => {
		debouncedSave.cancel()
	})

	// Add an input handler
	const handleInput = () => {
		debouncedSave()
	}
</script>

<div class={containerClasses}>
	<p class="text-xs text-brand-gray-4 -mt-2 lg:-mt-4 -mb-1 lg:-mb-2 h-4">{tripTitle ?? ''}</p>
	<input
		type="text"
		name="title"
		bind:this={inputElement}
		class={condensedClasses}
		value={title === 'New Trip' || title === 'Add a Name' || title === null || title === ''
			? 'Add a Name'
			: fixTitleCharacters(title)}
		onblur={saveTitle}
		oninput={handleInput}
		onkeydown={handleKeydown}
	/>
	<span class={`text-red-500 text-xs h-[5px] ${error == '' && 'opacity-0'}`}>{error}</span>
</div>
